import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttorney } from '../../context/AttorneyContext';
import { useMyInternalRequests, useForwardedResponses } from '../../hooks/useMessaging';
import RequestViaMarkoTMModal from './RequestViaMarkoTMModal';
import './MarkoTMResponses.css';

/**
 * MarkoTM Responses Component
 * For external attorneys to:
 * 1. See their pending requests to MarkoTM
 * 2. See responses forwarded back from clients
 * 3. Submit new requests to MarkoTM
 *
 * External attorneys cannot message clients directly - all communication goes through MarkoTM.
 */
function MarkoTMResponses() {
  const { t } = useTranslation('attorney');
  const { canCreateInternalRequests, canMessageClients, firmName } = useAttorney();
  const [activeTab, setActiveTab] = useState('pending');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);

  const { data: myRequestsData, isLoading: requestsLoading } = useMyInternalRequests();
  const { data: forwardedData, isLoading: forwardedLoading } = useForwardedResponses();

  const myRequests = myRequestsData?.requests || [];
  const forwardedResponses = forwardedData?.messages || [];

  const pendingRequests = myRequests.filter(r => r.status === 'pending' || r.status === 'client_contacted');
  const completedRequests = myRequests.filter(r => r.status === 'response_forwarded' || r.status === 'declined');

  // Access check - only external attorneys see this page
  if (canMessageClients) {
    // MarkoTM attorneys should use Client Messages instead
    return (
      <div className="markotm-responses-page">
        <div className="access-info">
          <h2>Internal Attorney</h2>
          <p>
            As a MarkoTM attorney, you can message clients directly.
            Please use the "Client Messages" section instead.
          </p>
        </div>
      </div>
    );
  }

  if (!canCreateInternalRequests) {
    return (
      <div className="markotm-responses-page">
        <div className="access-denied">
          <h2>Access Restricted</h2>
          <p>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const isLoading = requestsLoading || forwardedLoading;
  const displayItems = activeTab === 'pending' ? pendingRequests :
                       activeTab === 'completed' ? completedRequests :
                       forwardedResponses;

  return (
    <div className="markotm-responses-page">
      <div className="page-header">
        <div className="header-content">
          <h1>{t('attorney.markoTMResponses.title', 'MarkoTM Responses')}</h1>
          <p className="page-subtitle">
            {t('attorney.markoTMResponses.subtitle', 'Request client information through MarkoTM')}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setIsRequestModalOpen(true)}
        >
          {t('attorney.markoTMResponses.newRequest', 'Request via MarkoTM')}
        </button>
      </div>

      <div className="info-banner">
        <span className="info-icon">ℹ️</span>
        <p>
          {t('attorney.markoTMResponses.infoBanner',
            'As an external attorney, client communication goes through MarkoTM. Submit your information requests here, and MarkoTM will contact the client on your behalf.')}
        </p>
      </div>

      <div className="responses-tabs">
        <button
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          {t('attorney.markoTMResponses.pendingRequests', 'Pending Requests')}
          {pendingRequests.length > 0 && (
            <span className="tab-badge">{pendingRequests.length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'responses' ? 'active' : ''}`}
          onClick={() => setActiveTab('responses')}
        >
          {t('attorney.markoTMResponses.clientResponses', 'Client Responses')}
          {forwardedResponses.length > 0 && (
            <span className="tab-badge success">{forwardedResponses.length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          {t('attorney.markoTMResponses.completed', 'Completed')}
        </button>
      </div>

      <div className="responses-list">
        {isLoading ? (
          <div className="loading-state">Loading...</div>
        ) : displayItems.length === 0 ? (
          <div className="empty-state">
            {activeTab === 'pending'
              ? t('attorney.markoTMResponses.noPending', 'No pending requests to MarkoTM')
              : activeTab === 'responses'
              ? t('attorney.markoTMResponses.noResponses', 'No client responses yet')
              : t('attorney.markoTMResponses.noCompleted', 'No completed requests')}
          </div>
        ) : (
          displayItems.map((item) => (
            activeTab === 'responses' ? (
              <ForwardedResponseCard key={item.id} response={item} />
            ) : (
              <RequestCard key={item.id} request={item} />
            )
          ))
        )}
      </div>

      {isRequestModalOpen && (
        <RequestViaMarkoTMModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Request Card - shows pending/completed requests
 */
function RequestCard({ request }) {
  const { t } = useTranslation('attorney');
  const [expanded, setExpanded] = useState(false);

  const statusLabels = {
    pending: { icon: '⏳', label: 'Pending MarkoTM Action', color: 'status-pending' },
    client_contacted: { icon: '📧', label: 'Client Contacted', color: 'status-progress' },
    response_forwarded: { icon: '✅', label: 'Response Received', color: 'status-success' },
    declined: { icon: '❌', label: 'Declined', color: 'status-declined' }
  };

  const status = statusLabels[request.status] || statusLabels.pending;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={`request-card ${expanded ? 'expanded' : ''}`}>
      <div className="request-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="request-info">
          <span className={`request-status ${status.color}`}>
            {status.icon} {status.label}
          </span>
          <span className="request-title">{request.title}</span>
          <span className="request-application">
            Application: {request.applicationId}
            {request.metadata?.clientName && ` - ${request.metadata.clientName}`}
          </span>
        </div>
        <div className="request-date">
          {formatDate(request.createdAt)}
        </div>
      </div>

      {expanded && (
        <div className="request-card-body">
          <div className="request-content">
            <h4>{t('attorney.markoTMResponses.yourRequest', 'Your Request')}</h4>
            <p>{request.message}</p>
          </div>

          {request.requestData?.items && request.requestData.items.length > 0 && (
            <div className="request-items">
              <h4>{t('attorney.markoTMResponses.requestedItems', 'Requested Items')}</h4>
              <ul>
                {request.requestData.items.map((item, idx) => (
                  <li key={idx}>
                    <span className="item-type">[{item.type}]</span>
                    <span className="item-label">{item.label}</span>
                    {item.required && <span className="item-required">*</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {request.status === 'declined' && request.declineReason && (
            <div className="decline-info">
              <h4>{t('attorney.markoTMResponses.declineReason', 'Decline Reason')}</h4>
              <p>{request.declineReason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Forwarded Response Card - shows client responses forwarded by MarkoTM
 */
function ForwardedResponseCard({ response }) {
  const { t } = useTranslation('attorney');
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={`response-card ${expanded ? 'expanded' : ''}`}>
      <div className="response-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="response-info">
          <span className="response-icon">📬</span>
          <div className="response-meta">
            <span className="response-title">{response.title}</span>
            <span className="response-application">
              Application: {response.applicationId}
              {response.metadata?.clientName && ` - ${response.metadata.clientName}`}
            </span>
          </div>
        </div>
        <div className="response-date">
          <span className="date-label">{t('attorney.markoTMResponses.forwardedOn', 'Forwarded')}</span>
          <span>{formatDate(response.forwardedAt)}</span>
        </div>
      </div>

      {expanded && (
        <div className="response-card-body">
          <div className="response-content">
            <h4>{t('attorney.markoTMResponses.clientResponse', 'Client Response')}</h4>
            <p>{response.responseMessage || 'No message provided'}</p>
          </div>

          {response.requestData?.items && response.requestData.items.length > 0 && (
            <div className="response-items">
              <h4>{t('attorney.markoTMResponses.providedInformation', 'Provided Information')}</h4>
              <ul>
                {response.requestData.items.map((item, idx) => (
                  <li key={idx} className={item.response ? 'item-answered' : ''}>
                    <span className="item-label">{item.label}</span>
                    {item.response ? (
                      <span className="item-response">{item.response}</span>
                    ) : (
                      <span className="item-empty">Not provided</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {response.responseAttachments && response.responseAttachments.length > 0 && (
            <div className="response-attachments">
              <h4>{t('attorney.markoTMResponses.attachments', 'Attachments')}</h4>
              <ul>
                {response.responseAttachments.map((att, idx) => (
                  <li key={idx}>
                    <a href={att.blobUrl} target="_blank" rel="noopener noreferrer">
                      📎 {att.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MarkoTMResponses;
